// AdminDashboard.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Grid, Card, CardContent, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PaymentIcon from "@mui/icons-material/Payment";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { motion } from "framer-motion";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler
} from "chart.js";
import { getAdminDashboardData } from "../../services/reportService";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const brand = "#800b33"; // your system primary (maroon / deep burgundy)
const brandHover = "#6c0a2b"; // darker shade for hover states

const formatCurrency = (n) =>
  typeof n === "number" ? new Intl.NumberFormat("en-TZ").format(n) : n;

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.45, ease: "easeOut" },
  }),
};

const StatCard = ({ icon, label, value, index }) => (
  <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={index}>
    <Card
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: 3,
        boxShadow: 3,
        "&:hover": { boxShadow: 6, transform: "translateY(-4px)" },
        transition: "all .25s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            backgroundColor: `${brand}1A`, // subtle brand tint
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            {formatCurrency(value)}
          </Typography>
        </Box>
      </Box>
    </Card>
  </motion.div>
);

const AdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getAdminDashboardData();
        setDashboardData(data);
      } catch (err) {
        setError(err.message);
        console.error("Failed to fetch admin dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const stats = useMemo(() => [
    { label: t("Total Groups"), value: dashboardData?.stats?.total_groups || 0, icon: <GroupsIcon sx={{ fontSize: 28, color: brand }} /> },
    { label: t("Total Members"), value: dashboardData?.stats?.total_members || 0, icon: <PeopleAltIcon sx={{ fontSize: 28, color: brand }} /> },
    { label: t("New Groups (This Month)"), value: dashboardData?.stats?.new_groups_this_month || 0, icon: <GroupsIcon sx={{ fontSize: 28, color: "success.main" }} /> },
    { label: t("Total Transactions (This Month)"), value: dashboardData?.stats?.total_transactions_this_month || 0, icon: <PendingActionsIcon sx={{ fontSize: 28, color: brand }} /> },
  ], [dashboardData, t]);

  const recentGroups = useMemo(() => dashboardData?.recent_groups || [], [dashboardData]);

  const notifications = useMemo(() => dashboardData?.notifications || [], [dashboardData]);

  // Chart data (Loan disbursements vs Contributions over last 8 periods)
  const chartData = useMemo(() => {
    const labels = dashboardData?.chart_data?.labels || [];
    return {
      labels,
      datasets: [
        {
          label: t("Loan Disbursements"),
          data: dashboardData?.chart_data?.disbursements || [],
          borderColor: brand,
          backgroundColor: `${brand}24`,
          fill: true,
          tension: 0.35,
          pointRadius: 3,
        },
        {
          label: t("contributions"),
          data: dashboardData?.chart_data?.contributions || [],
          borderColor: "#0d47a1",
          backgroundColor: "rgba(13,71,161,.12)",
          fill: true,
          tension: 0.35,
          pointRadius: 3,
        },
      ],
    };
  }, [dashboardData, t]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" } },
    scales: {
      y: { beginAtZero: true, ticks: { callback: (v) => formatCurrency(v) } },
      x: { grid: { display: false } },
    },
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Title */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: brand }}>
          {t("Admin Dashboard")}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          {t("Manage the system and oversee all groups")}
        </Typography>
      </Box>

      {loading && <Typography>{t("Loading dashboard data...")}</Typography>}
      {error && <Typography color="error">{t("Failed to load dashboard: {{error}}", { error })}</Typography>}

      {!loading && !error && dashboardData && (
        <>
          {/* Top Stat Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {stats.map((s, i) => (
              <Grid item xs={12} sm={6} md={3} key={s.label}>
                <StatCard icon={s.icon} label={s.label} value={s.value} index={i} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Chart + Quick Actions */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                {t("Loan Disbursement vs Contributions (trend)")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ height: 300 }}>
                <Line data={chartData} options={chartOptions} />
              </Box>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} md={4}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.2}>
            <Card sx={{ p: 2, borderRadius: 3, boxShadow: 3 }}>
              <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                {t("Quick Actions")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                <Button
                  variant="contained"
                  onClick={() => navigate("/app/admin/users")}
                  sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                >
                  {t("User Management")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/app/reports/system-growth-metrics")}
                  sx={{ borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }}
                >
                  {t("System Reports")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/app/reports/audit-compliance")}
                  sx={{ borderColor: brand, color: brand, "&:hover": { borderColor: brandHover, color: brandHover } }}
                >
                  {t("View Audit Logs")}
                </Button>
              </Box>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Recent Registrations + Notifications */}
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item xs={12} md={7}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.4}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t("Recent Group Registrations")}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate("/app/desk-officer/groups")}
                    sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
                  >
                    {t("View All")}
                  </Button>
                </Box>
                <TableContainer component={Paper} elevation={0}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{t("Group")}</TableCell>
                        <TableCell>{t("Chair")}</TableCell>
                        <TableCell>{t("Members")}</TableCell>
                        <TableCell>{t("Status")}</TableCell>
                        <TableCell>{t("Date")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentGroups.length > 0 ? recentGroups.map((g, idx) => (
                        <TableRow key={g.id} hover>
                          <TableCell>{idx + 1}</TableCell>
                          <TableCell>{g.name}</TableCell>
                          <TableCell>{g.chair}</TableCell>
                          <TableCell>{g.members}</TableCell>
                          <TableCell>
                            <Chip
                              label={g.status}
                              size="small"
                              color={g.status === "Approved" ? "success" : "warning"}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>{g.date}</TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            {t("No recent group registrations found.")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Notifications styled like Chairperson Dashboard */}
        <Grid item xs={12} md={5}>
          <motion.div variants={fadeIn} initial="hidden" animate="visible" custom={1.6}>
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700, color: brand }}>
                  {t("layout.notificationsTitle")}
                </Typography>
                {notifications.length > 0 ? (
                  notifications.map((n) => (
                    <motion.div
                      key={n._id} // Assuming a unique _id from the database
                      whileHover={{ scale: 1.02 }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "12px",
                        borderRadius: "10px",
                        background: "#f9fafb",
                        marginBottom: "10px",
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <NotificationsActiveIcon sx={{ color: brand, mr: 1.5 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {n.text}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {n.time}
                        </Typography>
                      </Box>
                    </motion.div>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                    {t("layout.noNotifications")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Footer */}
      <Typography
        variant="caption"
        sx={{ display: "block", textAlign: "center", color: "text.secondary", mt: 4 }}
      >
        © {new Date().getFullYear()} Savvy Community
      </Typography>
    </Box>
  );
};

export default AdminDashboard;
